import React, { useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { I18nProvider } from './i18n';
import { PublicAuthProvider } from './context/PublicAuth';
import Navbar from './components/site/Navbar';
import Hero from './components/site/Hero';
import Partners from './components/site/Partners';
import ClientLogos from './components/site/ClientLogos';
import WelcomeModal from './components/site/WelcomeModal';
import ServicesSection from './components/site/ServicesSection';
import Portfolio from './components/site/Portfolio';
import TechStack from './components/site/TechStack';
import Industries from './components/site/Industries';
import About from './components/site/About';
import Process from './components/site/Process';
import Insights from './components/site/Insights';
import FAQ from './components/site/FAQ';
import CTA from './components/site/CTA';
import ContactMap from './components/site/ContactMap';
import YouTubeSection from './components/site/YouTubeSection';
import TrustBar from './components/site/TrustBar';
import TestimonialWall from './components/site/TestimonialWall';
import AboutUsPage from './pages/AboutUsPage';
import PressPage from './pages/PressPage';
import WhyHipro from './components/site/WhyHipro';
import Footer from './components/site/Footer';
import BookCallModal from './components/site/BookCallModal';
import ChatWidget from './components/site/ChatWidget';
import { Toaster } from './components/ui/toaster';
import CaseStudyPage from './pages/CaseStudyPage';
import PricingPage from './pages/PricingPage';
import SubmitTestimonialPage from './pages/SubmitTestimonialPage';
import AdminPage from './pages/AdminPage';
import BookMeetingPage from './pages/BookMeetingPage';
import { CareersPage, JobDetailPage } from './pages/CareersPages';
import { BlogListPage, BlogPostPage } from './pages/BlogPages';
import NotFoundPage from './pages/NotFoundPage';
import PortfolioPage from './pages/PortfolioPage';
import { PrivacyPolicyPage, TermsPage, CookiePolicyPage } from './pages/LegalPages';
import ServiceDetailPage from './pages/ServiceDetailPage';
import HrmsLogin from './pages/hrms/HrmsLogin';
import HrmsLayout from './pages/hrms/HrmsLayout';
import HrmsDashboard from './pages/hrms/HrmsDashboard';
import HrmsReset from './pages/hrms/HrmsReset';
import JobsPage from './pages/hrms/JobsPage';
import PipelinePage from './pages/hrms/PipelinePage';
import { CandidatesPage, NotificationsPage } from './pages/hrms/OtherHrmsPages';
import InterviewsPage from './pages/hrms/InterviewsPage';
import OffersPage from './pages/hrms/OffersPage';
import { EmployeesPage, LeavesAdminPage } from './pages/hrms/EmployeesPage';
import MePage from './pages/hrms/MePage';
import AnalyticsPage from './pages/hrms/AnalyticsPage';
import AttendancePage from './pages/hrms/AttendancePage';
import PayrollPage from './pages/hrms/PayrollPage';
import SettingsPage from './pages/hrms/SettingsPage';
import ReferralsAdminPage from './pages/hrms/ReferralsAdminPage';
import CalendarPage from './pages/hrms/CalendarPage';
import OnboardingPage from './pages/hrms/OnboardingPage';
import OnboardingAdminPage from './pages/hrms/OnboardingAdminPage';
import HandbookPage from './pages/hrms/HandbookPage';
import PublicOfferPage from './pages/PublicOfferPage';
import AuthCallback from './pages/AuthCallback';
import AccountPage from './pages/AccountPage';
import { LoginPage, VerifyEmailPage, ResetPasswordPage } from './pages/AuthPages';
import Seo, { ORG_JSONLD, WEBSITE_JSONLD } from './components/Seo';
import { faqs } from './mock';

const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const Home = () => {
  const [callOpen, setCallOpen] = useState(false);
  const open = () => setCallOpen(true);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Seo
        title="Hipro Technologies — Innovate. Build. Scale | Software, AI & Digital Marketing"
        description="Hipro Technologies — Innovate. Build. Scale. End-to-end IT services since 2015: web & mobile development, custom software, cloud engineering, AI automation and digital marketing. Offices in India, USA and UK."
        path="/"
        jsonLd={[ORG_JSONLD, WEBSITE_JSONLD, FAQ_JSONLD]}
      />
      <Navbar onBookCall={open} />
      <main>
        <Hero />
        <TrustBar />
        <ClientLogos />
        <Partners />
        <About />
        <ServicesSection onBookCall={open} />
        <Portfolio limit={6} />
        <TestimonialWall />
        <WhyHipro />
        <TechStack />
        <Industries onBookCall={open} />
        <Process />
        <Insights />
        <FAQ />
        <YouTubeSection />
        <ContactMap />
        <CTA onBookCall={open} />
      </main>
      <Footer />
      <ChatWidget />
      <BookCallModal open={callOpen} onOpenChange={setCallOpen} />
      <WelcomeModal />
    </div>
  );
};

// Synchronous hash detection to catch #session_id=... before normal routing
function AppRouter() {
  const location = useLocation();
  // read from useLocation().hash so React re-renders when we clear the fragment
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/portfolio" element={<PortfolioPage />} />
      <Route path="/about-us" element={<AboutUsPage />} />
      <Route path="/press" element={<PressPage />} />
      <Route path="/services/:slug" element={<ServiceDetailPage />} />
      <Route path="/portfolio/:slug" element={<CaseStudyPage />} />
      <Route path="/submit-testimonial" element={<SubmitTestimonialPage />} />
      <Route path="/book-meeting" element={<BookMeetingPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/careers/:jobId" element={<JobDetailPage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsPage />} />
      <Route path="/cookie-policy" element={<CookiePolicyPage />} />
      <Route path="/offer/:token" element={<PublicOfferPage />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/hrms/login" element={<HrmsLogin />} />
      <Route path="/hrms/reset-password" element={<HrmsReset />} />
      <Route path="/hrms" element={<HrmsLayout />}>
        <Route index element={<HrmsDashboard />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="pipeline" element={<PipelinePage />} />
        <Route path="interviews" element={<InterviewsPage />} />
        <Route path="offers" element={<OffersPage />} />
        <Route path="candidates" element={<CandidatesPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="payroll" element={<PayrollPage />} />
        <Route path="leaves" element={<LeavesAdminPage />} />
        <Route path="me" element={<MePage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="referrals" element={<ReferralsAdminPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="onboarding" element={<OnboardingAdminPage />} />
        <Route path="me/onboarding" element={<OnboardingPage />} />
        <Route path="me/handbook" element={<HandbookPage />} />
        <Route path="handbook" element={<HandbookPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <BrowserRouter>
          <PublicAuthProvider>
            <AppRouter />
            <Toaster />
          </PublicAuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </div>
  );
}

export default App;
